/**
 * Pure helpers for MapLibre bike station sources, layers, and GeoJSON mapping.
 */

import type { Feature, FeatureCollection } from "geojson";
import type maplibregl from "maplibre-gl";

import { MAP_BIKE_STATIONS_LAYER_ZOOM } from "../../../config/global.configuration";
import sharingBikeIcon from "../../../image/icons/map/transportIcons/SharingBikeIcon3.svg";
import { colorPalette } from "../../../theme/ColorPalette";
import type { BikeStationDTO } from "../../../types/dtos/api/bike-station.dto";
import {
    BIKE_STATIONS_BACKGROUND_LAYER_ID,
    BIKE_STATIONS_LAYER_ID,
    BIKE_STATIONS_SOURCE_ID,
    BIKE_STATION_EMPTY_ICON_IMAGE_ID,
    BIKE_STATION_ICON_IMAGE_ID,
} from "../mapLayerIds";
import {
    getStopLabelTextColor,
    getStopLabelTextHaloColor,
} from "../stops/stopMapLayers.helpers";
import { resolveBikeStationDisplayName } from "./bikeStationPopupModern.tools";

/** Empty GeoJSON used when bike stations cannot be loaded. */
export const EMPTY_BIKE_STATIONS_FEATURE_COLLECTION: FeatureCollection = {
    "type":     "FeatureCollection",
    "features": [],
};

/**
 * Draws the source SVG into an offscreen canvas, optionally tinting it for the active theme
 * and adding a cross when the station has no available bikes.
 *
 * @param isDarkUi Whether dark UI palette is active.
 * @param isNoBikes Whether to draw a cross over the icon.
 * @returns A data URL for the generated icon.
 */
const buildBikeStationIconDataUrl = async( isDarkUi: boolean, isNoBikes: boolean ): Promise<string> => {
    const sourceImage = await new Promise<HTMLImageElement>( ( resolve, reject ) => {
        const img = new Image();

        img.onload = () => resolve( img );
        img.onerror = () => reject( new Error( `Failed to load bike station icon: ${ sharingBikeIcon }` ) );

        img.src = sharingBikeIcon;
    } );

    const canvas = document.createElement( "canvas" );

    canvas.width = sourceImage.naturalWidth || 64;
    canvas.height = sourceImage.naturalHeight || 64;

    const context = canvas.getContext( "2d" );

    if ( context === null ) {
        throw new Error( "Failed to get 2D context for bike station icon canvas" );
    }

    context.drawImage( sourceImage, 0, 0 );

    if ( isDarkUi ) {
        context.globalCompositeOperation = "source-in";
        context.fillStyle = "#ffffff";
        context.fillRect( 0, 0, canvas.width, canvas.height );
    }

    if ( isNoBikes ) {
        const lineWidth = Math.min( canvas.width, canvas.height ) * 0.12;

        context.globalCompositeOperation = "source-over";
        context.strokeStyle = "#DC2626";
        context.lineWidth = lineWidth;
        context.lineCap = "round";
        context.beginPath();
        context.moveTo( 0, 0 );
        context.lineTo( canvas.width, canvas.height );
        context.stroke();
    }

    return canvas.toDataURL( "image/png" );
};

/**
 * Loads an HTMLImageElement from a data URL.
 *
 * @param dataUrl Image data URL.
 * @param errorMessage Error message on load failure.
 * @returns Promise resolving to the loaded image.
 */
const loadImageFromDataUrl = ( dataUrl: string, errorMessage: string ): Promise<HTMLImageElement> => new Promise( ( resolve, reject ) => {
    const img = new Image();

    img.onload = () => resolve( img );
    img.onerror = () => reject( new Error( errorMessage ) );

    img.src = dataUrl;
} );

/**
 * Loads the bike station SVG icons into MapLibre, tinted for the active UI theme.
 *
 * @param map Target MapLibre map instance.
 * @param isDarkUi Whether dark UI palette is active.
 * @returns Promise resolved once the images are registered.
 */
export const loadBikeStationMapIcon = async( map: maplibregl.Map, isDarkUi: boolean ): Promise<void> => {
    if ( map.hasImage( BIKE_STATION_ICON_IMAGE_ID ) ) {
        map.removeImage( BIKE_STATION_ICON_IMAGE_ID );
    }

    if ( map.hasImage( BIKE_STATION_EMPTY_ICON_IMAGE_ID ) ) {
        map.removeImage( BIKE_STATION_EMPTY_ICON_IMAGE_ID );
    }

    const [ dataUrl, emptyDataUrl ] = await Promise.all( [
        buildBikeStationIconDataUrl( isDarkUi, false ),
        buildBikeStationIconDataUrl( isDarkUi, true ),
    ] );

    const [ tintedImage, emptyImage ] = await Promise.all( [
        loadImageFromDataUrl( dataUrl, "Failed to load tinted bike station icon" ),
        loadImageFromDataUrl( emptyDataUrl, "Failed to load crossed-out bike station icon" ),
    ] );

    map.addImage( BIKE_STATION_ICON_IMAGE_ID, tintedImage );
    map.addImage( BIKE_STATION_EMPTY_ICON_IMAGE_ID, emptyImage );
};

/**
 * Ensures the bike station GeoJSON source exists on the map.
 *
 * @param map Target MapLibre map instance.
 */
export const ensureBikeStationSources = ( map: maplibregl.Map ): void => {
    if ( map.getSource( BIKE_STATIONS_SOURCE_ID ) === undefined ) {
        map.addSource( BIKE_STATIONS_SOURCE_ID, {
            "type": "geojson",
            "data": EMPTY_BIKE_STATIONS_FEATURE_COLLECTION,
        } );
    }
};

/**
 * Resolves the bike station background circle fill color for the current UI theme.
 *
 * @param isDarkUi Whether dark UI palette is active.
 * @returns Circle fill color.
 */
const getBikeStationCircleColor = ( isDarkUi: boolean ): string => (
    isDarkUi ? colorPalette.dark.overlayCardBackgroundStrong : "#ffffff"
);


/**
 * Adds bike station symbol layers when missing.
 *
 * @param map Target MapLibre map instance.
 * @param isDarkUi Whether dark UI palette is active.
 */
export const ensureBikeStationLayers = ( map: maplibregl.Map, isDarkUi: boolean ): void => {
    if ( map.getLayer( BIKE_STATIONS_BACKGROUND_LAYER_ID ) === undefined ) {
        map.addLayer( {
            "id":      BIKE_STATIONS_BACKGROUND_LAYER_ID,
            "type":    "circle",
            "source":  BIKE_STATIONS_SOURCE_ID,
            "minzoom": MAP_BIKE_STATIONS_LAYER_ZOOM.minZoom,
            "paint":   {
                "circle-radius":       12,
                "circle-color":        getBikeStationCircleColor( isDarkUi ),
                "circle-stroke-width": 2,
                "circle-stroke-color": "#11d39c",
            },
        } );
    }

    if ( map.getLayer( BIKE_STATIONS_LAYER_ID ) === undefined ) {
        map.addLayer( {
            "id":      BIKE_STATIONS_LAYER_ID,
            "type":    "symbol",
            "source":  BIKE_STATIONS_SOURCE_ID,
            "minzoom": MAP_BIKE_STATIONS_LAYER_ZOOM.minZoom,
            "layout":  {
                "icon-image": [
                    "case",
                    [ "==", [ "get", "bikesAvailable" ], 0 ],
                    BIKE_STATION_EMPTY_ICON_IMAGE_ID,
                    BIKE_STATION_ICON_IMAGE_ID,
                ],
                "icon-size":             0.03,
                "icon-allow-overlap":    true,
                "icon-ignore-placement": true,
                "text-field":            [ "get", "name" ],
                "text-anchor":           "top",
                "text-offset":           [ 0, 1.5 ],
                "text-font":             [ "Open Sans Semibold", "Arial Unicode MS Bold" ],
                "text-size":             10,
                "text-allow-overlap":    true,
            },
            "paint": {
                "text-color":      getStopLabelTextColor( isDarkUi ),
                "text-halo-color": getStopLabelTextHaloColor( isDarkUi ),
                "text-halo-width": 2,
            },
        } );
    }
};

/**
 * Updates bike station map layer colors and icon tint when the UI theme changes.
 *
 * @param map Target MapLibre map instance.
 * @param isDarkUi Whether dark UI palette is active.
 * @returns Promise resolved once the icon and paints are updated.
 */
export const applyBikeStationLayerTheme = async( map: maplibregl.Map, isDarkUi: boolean ): Promise<void> => {
    if ( map.getLayer( BIKE_STATIONS_BACKGROUND_LAYER_ID ) !== undefined ) {
        map.setPaintProperty( BIKE_STATIONS_BACKGROUND_LAYER_ID, "circle-color", getBikeStationCircleColor( isDarkUi ) );
    }

    if ( map.getLayer( BIKE_STATIONS_LAYER_ID ) !== undefined ) {
        map.setPaintProperty( BIKE_STATIONS_LAYER_ID, "text-color", getStopLabelTextColor( isDarkUi ) );
        map.setPaintProperty( BIKE_STATIONS_LAYER_ID, "text-halo-color", getStopLabelTextHaloColor( isDarkUi ) );
    }

    await loadBikeStationMapIcon( map, isDarkUi );
};

/**
 * Maps a list of bike station DTOs into a GeoJSON feature collection.
 *
 * @remarks Stations with `bikesAvailable === 0` are kept and rendered with a crossed-out icon.
 * @param stations Bike station DTOs from the bbox endpoint.
 * @returns GeoJSON FeatureCollection of Point features.
 */
export const bikeStationsToFeatureCollection = ( stations: BikeStationDTO[] ): FeatureCollection => ( {
    "type":     "FeatureCollection",
    "features": stations.map<BikeStationFeature>( ( station ) => ( {
        "type":     "Feature",
        "geometry": {
            "type":        "Point",
            "coordinates": [ station.lon, station.lat ],
        },
        "properties": {
            "id":             station.stationId,
            "name":           resolveBikeStationDisplayName( station.name ),
            "operator":       station.operator,
            "capacity":       station.capacity,
            "bikesAvailable": station.bikesAvailable,
        },
    } ) ),
} );

type BikeStationFeature = Feature<
    GeoJSON.Point,
    {
        "id":             string;
        "name":           string;
        "operator":       string;
        "capacity":       number | null;
        "bikesAvailable": number | null;
    }
>;
